import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import LandingView from './views/LandingView.vue';
import LoginView from './views/LoginView.vue';
import ModelsView from './views/ModelsView.vue';
import DashboardView from './views/DashboardView.vue';
import KeysView from './views/KeysView.vue';
import UsageView from './views/UsageView.vue';
import BillingView from './views/BillingView.vue';
import PartnerView from './views/PartnerView.vue';
import DocsView from './views/DocsView.vue';
import ChatView from './views/ChatView.vue';
import SettingsView from './views/SettingsView.vue';
import LedgerView from './views/LedgerView.vue';
import TopupView from './views/TopupView.vue';
import { useAuthStore } from './stores/auth';

const routes: RouteRecordRaw[] = [
  // ── Public (marketing layout) — no session required ──────────────────────
  { path: '/', name: 'home', component: LandingView, meta: { public: true, layout: 'marketing', title: 'Open Gonka' } },
  { path: '/docs', name: 'docs', component: DocsView, meta: { public: true, layout: 'marketing', title: 'Docs' } },
  { path: '/models', name: 'models', component: ModelsView, meta: { public: true, layout: 'marketing', title: 'Models' } },

  // ── Auth (bare layout) ───────────────────────────────────────────────────
  { path: '/login', name: 'login', component: LoginView, meta: { public: true, layout: 'bare', title: 'Sign in' } },

  // ── Cabinet (app layout) — session required ──────────────────────────────
  { path: '/dashboard', name: 'dashboard', component: DashboardView, meta: { layout: 'app', title: 'Overview' } },
  { path: '/keys', name: 'keys', component: KeysView, meta: { layout: 'app', title: 'API Keys' } },
  { path: '/usage', name: 'usage', component: UsageView, meta: { layout: 'app', title: 'Usage' } },
  { path: '/billing', name: 'billing', component: BillingView, meta: { layout: 'app', title: 'Billing' } },
  { path: '/partner', name: 'partner', component: PartnerView, meta: { layout: 'app', title: 'Partner' } },
  { path: '/chat', name: 'chat', component: ChatView, meta: { layout: 'app', title: 'Chat' } },
  { path: '/settings', name: 'settings', component: SettingsView, meta: { layout: 'app', title: 'Settings' } },
  { path: '/ledger', name: 'ledger', component: LedgerView, meta: { layout: 'app', title: 'Ledger' } },
  // /billing/topup is the canonical Top-Up subpage (T05). Sidebar 'Billing'
  // (to: '/billing') stays highlighted on the child path because vue-router's
  // default active-class match is prefix-inclusive.
  { path: '/billing/topup', name: 'topup', component: TopupView, meta: { layout: 'app', title: 'Top-up' } },
  // Backwards-compat redirect: legacy `/topup` links resolve to /billing/topup.
  { path: '/topup', redirect: '/billing/topup' },
  // Unknown paths land on the public landing.
  { path: '/:pathMatch(.*)*', redirect: '/' },
];

export const router = createRouter({
  history: createWebHistory('/'),
  routes,
  scrollBehavior(to) {
    // Support in-page anchors (e.g. /#pricing) and reset scroll on navigation.
    if (to.hash) return { el: to.hash, behavior: 'smooth' };
    return { top: 0 };
  },
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  if (!auth.ready) {
    await auth.bootstrap();
  }
  const isPublic = to.meta.public === true;

  // Guest hitting a private route → login, preserving the intended path.
  if (!isPublic && !auth.me) {
    return { name: 'login', query: to.fullPath !== '/' ? { next: to.fullPath } : undefined };
  }

  // Signed-in user on the login page → dashboard (or wherever they were headed).
  // Public marketing pages (/, /docs, /models) stay accessible while signed in.
  if (to.name === 'login' && auth.me) {
    const next = typeof to.query.next === 'string' ? to.query.next : '/dashboard';
    return next;
  }

  return true;
});
